import type { Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isSupabaseConfigured, supabase } from './supabase';

type AuthState = {
  isLoaded: boolean;
  isSignedIn: boolean;
  session: Session | null;
  user: User | null;
  signOut: () => Promise<void>;
  signInWithKakao: (redirectTo?: string) => Promise<void>;
  signInWithGoogle: (redirectTo?: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
};

const MOCK_USER = {
  id: '00000000-0000-0000-0000-000000000001',
  email: 'dev@lashpic.local',
  user_metadata: { full_name: '테스트 원장', name: '테스트 원장' },
  app_metadata: { provider: 'mock' },
} as unknown as User;

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setIsLoaded(true);
      return;
    }
    let cancelled = false;
    // 안전망: Supabase Auth 가 5초 안에 응답 없으면 비로그인 상태로 진행해 UI 잠금 방지
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      console.warn('[AuthProvider] getSession 5s timeout — Supabase Auth 응답 없음. 비로그인 상태로 진행.');
      setIsLoaded(true);
    }, 5000);
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('[AuthProvider] getSession error:', error);
        setSession(data.session);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[AuthProvider] getSession rejected:', err);
      })
      .finally(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setIsLoaded(true);
      });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (cancelled) return;
      setSession(s);
      setIsLoaded(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(() => {
    if (!isSupabaseConfigured) {
      return {
        isLoaded: true,
        isSignedIn: true,
        session: null,
        user: MOCK_USER,
        signOut: async () => {
          console.info('[Lashpic DEV] signOut() mocked');
        },
        signInWithKakao: async () => {
          console.warn('[Lashpic DEV] Supabase 미설정 — 카카오 로그인 mocked');
        },
        signInWithGoogle: async () => {
          console.warn('[Lashpic DEV] Supabase 미설정 — 구글 로그인 mocked');
        },
        getAccessToken: async () => 'dev-token',
      };
    }

    return {
      isLoaded,
      isSignedIn: Boolean(session),
      session,
      user: session?.user ?? null,
      signOut: async () => {
        await supabase?.auth.signOut();
      },
      signInWithKakao: async (redirectTo?: string) => {
        await supabase?.auth.signInWithOAuth({
          provider: 'kakao',
          options: {
            redirectTo: redirectTo ?? `${window.location.origin}/app/home`,
            scopes: 'profile_nickname account_email',
          },
        });
      },
      signInWithGoogle: async (redirectTo?: string) => {
        await supabase?.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectTo ?? `${window.location.origin}/app/home`,
          },
        });
      },
      getAccessToken: async () => session?.access_token ?? null,
    };
  }, [isLoaded, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useUser() {
  const { user, isLoaded, isSignedIn } = useAuth();
  return { user, isLoaded, isSignedIn };
}
